import React, { useState, useEffect, useMemo } from 'react';
import { 
  Search, BookOpen, Sparkles, Globe, ChevronDown, 
  LifeBuoy, CheckCircle, Shield, Users, MessageSquare,
  ArrowRight, ExternalLink, Mail, Phone, Box
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
// Custom Alert Component
const Alert = ({ children }) => (
  <div className="bg-primary-50 border-l-4 border-primary-600 p-4 rounded-md">
    {children}
  </div>
);

const categories = [
  {
    id: 'platform',
    title: 'Platform Features',
    icon: Sparkles,
    description: 'Core functionalities and capabilities'
  },
  {
    id: 'security',
    title: 'Security & Compliance',
    icon: Shield,
    description: 'Data protection and regulatory compliance'
  },
  {
    id: 'implementation',
    title: 'Implementation',
    icon: CheckCircle,
    description: 'Setup, integration, and deployment'
  },
  {
    id: 'support',
    title: 'Support & Training',
    icon: LifeBuoy,
    description: '24/7 assistance and resources'
  }
];

// Same FAQ data structure as before
const faqs = {
  platform: [
    {
      question: "What is FlexCOA's eCOA platform?",
      answer: "FlexCOA is a comprehensive electronic Clinical Outcome Assessment (eCOA) platform designed to modernize clinical trials. Our solution includes:",
      details: [
        "Web and mobile interfaces for patient-reported outcomes (PRO)",
        "Clinician-reported outcomes (ClinRO) with advanced workflow management",
        "Observer-reported outcomes (ObsRO) with real-time data capture",
        "Performance outcome assessments (PerfO) with multimedia capabilities",
        "Offline data collection with automatic synchronization"
      ]
    },
    {
      question: "What devices and platforms are supported?",
      answer: "FlexCOA supports a wide range of devices and platforms to ensure maximum accessibility:",
      details: [
        "iOS and Android mobile applications",
        "Web-based interface for desktop and tablet access",
        "BYOD (Bring Your Own Device) support",
        "Provisioned device management",
        "Cross-platform data synchronization"
      ]
    }
  ],
  security: [
    {
      question: "How does FlexCOA ensure data security and privacy?",
      answer: "We implement comprehensive security measures including:",
      details: [
        "End-to-end encryption for all data transmission",
        "HIPAA, GDPR, and CCPA compliance",
        "Regular third-party security audits",
        "Role-based access control (RBAC)",
        "Audit trails and activity logging"
      ]
    }
  ],
  implementation: [
    {
      question: "What is the typical implementation timeline?",
      answer: "Implementation timelines vary based on study complexity, but typically include:",
      details: [
        "Initial setup and configuration: 2-4 weeks",
        "Integration with existing systems: 1-2 weeks",
        "Validation and testing: 2-3 weeks",
        "User training and documentation: 1-2 weeks",
        "Go-live support and monitoring: Ongoing"
      ]
    }
  ],
  support: [
    {
      question: "What support options are available?",
      answer: "We offer comprehensive support through multiple channels:",
      details: [
        "24/7 technical support via phone, email, and chat",
        "Dedicated project managers for each study",
        "Online training portal with video tutorials",
        "Regular check-ins and progress reviews",
        "Emergency support hotline"
      ]
    }
  ]
};

const FAQItem = ({ item, isOpen, onToggle }) => {
  return (
    <div className="border-b border-gray-200 dark:border-gray-700 last:border-0">
      <button
        onClick={onToggle}
        className="w-full py-6 flex justify-between items-start text-left focus:outline-none group"
      >
        <span className="text-lg font-medium text-gray-900 group-hover:text-primary-600 transition-colors pr-8">
          {item.question}
        </span>
        <ChevronDown
          className={`w-5 h-5 text-primary-600 transform transition-transform duration-300 flex-shrink-0 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="pb-6">
              <p className="text-gray-600 mb-4">{item.answer}</p>
              {item.details && (
                <ul className="space-y-2">
                  {item.details.map((detail, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary-600 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">{detail}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const CategoryCard = ({ category, isActive, onClick }) => (
  <motion.button
    whileHover={{ y: -4 }}
    onClick={onClick}
    className={`w-full text-left p-6 rounded-2xl transition-all duration-300 ${
      isActive 
        ? 'bg-primary-600 text-white shadow-lg dark:shadow-gray-900/20'
        : 'bg-white dark:bg-gray-800 shadow-md dark:shadow-gray-900/20 hover:shadow-xl dark:hover:shadow-gray-900/20'
    }`}
  >
    <category.icon className={`w-8 h-8 mb-4 ${isActive ? 'text-white' : 'text-primary-600'}`} />
    <h3 className={`text-xl font-semibold mb-2 ${isActive ? 'text-white' : 'text-gray-900'}`}>
      {category.title}
    </h3>
    <p className={isActive ? 'text-white text-opacity-90' : 'text-gray-600'}>
      {category.description}
    </p>
  </motion.button>
);

const ContactSection = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
    <div className="bg-primary-600 rounded-2xl p-8 text-white">
      <h3 className="text-2xl font-semibold mb-4">Need immediate assistance?</h3>
      <div className="space-y-4">
        <div className="flex items-center">
          <Phone className="w-5 h-5 mr-3" />
          <span>+1 (888) 123-4567</span>
        </div>
        <div className="flex items-center">
          <Mail className="w-5 h-5 mr-3" />
          <span>support@flexcoa.com</span>
        </div>
        <div className="flex items-center">
          <MessageSquare className="w-5 h-5 mr-3" />
          <span>Live Chat Available</span>
        </div>
      </div>
    </div>
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg dark:shadow-gray-900/20">
      <h3 className="text-2xl font-semibold text-gray-900 mb-4">Additional Resources</h3>
      <div className="space-y-4">
        <a href="#" className="flex items-center group text-gray-600 hover:text-primary-600">
          <BookOpen className="w-5 h-5 mr-3" />
          <span>Documentation</span>
          <ArrowRight className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" />
        </a>
        <a href="#" className="flex items-center group text-gray-600 hover:text-primary-600">
          <Users className="w-5 h-5 mr-3" />
          <span>Community Forum</span>
          <ArrowRight className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" />
        </a>
        <a href="#" className="flex items-center group text-gray-600 hover:text-primary-600">
          <ExternalLink className="w-5 h-5 mr-3" />
          <span>Video Tutorials</span>
          <ArrowRight className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" />
        </a>
      </div>
    </div>
  </div>
);

const FAQPage = () => {
  const [activeCategory, setActiveCategory] = useState('platform');
  const [searchTerm, setSearchTerm] = useState('');
  const [openItems, setOpenItems] = useState(new Set());
  const [searchResults, setSearchResults] = useState([]);

  const allFaqs = useMemo(() => {
    return Object.entries(faqs).flatMap(([category, items]) =>
      items.map(item => ({ ...item, category }))
    );
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const results = allFaqs.filter(faq =>
        faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
        faq.details?.some(detail => 
          detail.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  }, [searchTerm, allFaqs]);

  const toggleItem = (index) => {
    setOpenItems(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const currentFaqs = searchTerm ? searchResults : faqs[activeCategory] || [];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-800">
      <div className="bg-primary-600 pt-24 pb-32 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            How can we help you?
          </motion.h1>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="max-w-2xl mx-auto"
          >
            <div className="relative">
              <input
                type="text"
                placeholder="Search for answers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-white bg-white bg-opacity-10 text-white placeholder-white placeholder-opacity-75 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 transition duration-300"
              />
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white" size={20} />
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 -mt-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {categories.map((category) => (
            <CategoryCard
              key={category.id}
              category={category}
              isActive={activeCategory === category.id && !searchTerm}
              onClick={() => {
                setActiveCategory(category.id);
                setSearchTerm('');
              }}
            />
          ))}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-gray-900/20 p-8 mb-12">
          {searchTerm && (
            <div className="mb-6">
              <Alert>
                Found {searchResults.length} results for "{searchTerm}"
              </Alert>
            </div>
          )}

          <div className="space-y-2">
            {currentFaqs.map((faq, index) => (
              <FAQItem
                key={index}
                item={faq}
                isOpen={openItems.has(index)}
                onToggle={() => toggleItem(index)}
              />
            ))}
          </div>

          {currentFaqs.length === 0 && (
            <div className="text-center py-12">
              <LifeBuoy className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No matching questions found</h3>
              <p className="text-gray-600">Try adjusting your search terms or browse by category</p>
            </div>
          )}
        </div>

        <ContactSection />
      </div>
    </div>
  );
};

export default FAQPage;